/**
 * FilterVisibilityContext — CR-4802.
 *
 * Which filter controls the viewer's tenant shows: Warehouse, Division,
 * Supplier Code, Supplier Type, Commodity Group/Type, Historical/Live and
 * Company Country. The backend resolves it once per viewer
 * (`/core/settings/filter-visibility/`): the tenant's declared Admin Setup
 * toggles, detected defaults where nothing is declared, the union of a
 * supplier's customers, and the switched tenant for a platform admin.
 *
 * Every filter panel reads the same answer through `useFilterVisibility`, so
 * a hidden filter is hidden on every page — the "consistent across all pages"
 * criterion the per-page rollout failed twice.
 *
 * Fails OPEN: until the answer arrives, and if it never does, every filter is
 * visible. A backend blip must never narrow a panel.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { coreAPI } from '../lib/api';
import type { FilterVisibilityPayload, FilterVisibilitySignals } from '../lib/api';
import { useAuth } from './AuthContext';
import { useView } from './ViewContext';

/** CamelCase view of the backend signals, as the panels read them. */
export interface FilterVisibilityFlags {
  warehouse: boolean;
  division: boolean;
  supplierCode: boolean;
  supplierType: boolean;
  commodity: boolean;
  historicalLive: boolean;
  companyCountry: boolean;
}

export const ALL_VISIBLE: FilterVisibilityFlags = {
  warehouse: true,
  division: true,
  supplierCode: true,
  supplierType: true,
  commodity: true,
  historicalLive: true,
  companyCountry: true,
};

export function toFlags(signals: FilterVisibilitySignals): FilterVisibilityFlags {
  return {
    warehouse: signals.warehouse,
    division: signals.division,
    supplierCode: signals.supplier_code,
    supplierType: signals.supplier_type,
    commodity: signals.commodity,
    historicalLive: signals.historical_live,
    companyCountry: signals.company_country,
  };
}

export interface FilterVisibilityContextType {
  /** What the panels gate on. */
  flags: FilterVisibilityFlags;
  /** The detected defaults, for the Admin Setup card's "detected" badges. */
  detected: FilterVisibilityFlags;
  /** Signals the tenant has declared explicitly (backend keys). */
  overrides: Partial<Record<keyof FilterVisibilitySignals, boolean>>;
  /** True when the viewer may declare the shape (tenant / platform admin). */
  editable: boolean;
  /** True once the backend has answered (or definitively failed). */
  ready: boolean;
  /** Re-fetch, e.g. after the Admin Setup card saves. */
  refresh: () => Promise<void>;
}

const DEFAULT_VALUE: FilterVisibilityContextType = {
  flags: ALL_VISIBLE,
  detected: ALL_VISIBLE,
  overrides: {},
  editable: false,
  ready: false,
  refresh: async () => undefined,
};

const FilterVisibilityContext = createContext<FilterVisibilityContextType>(DEFAULT_VALUE);

/**
 * Provides the viewer's filter visibility to the whole app. Re-resolves when
 * the user or the view context changes (a platform admin switching tenant).
 */
export function FilterVisibilityProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { viewKey } = useView();
  const [payload, setPayload] = useState<FilterVisibilityPayload | null>(null);
  const [ready, setReady] = useState(false);

  const load = useCallback(async () => {
    if (!user) {
      setPayload(null);
      setReady(false);
      return;
    }
    try {
      const response = await coreAPI.getFilterVisibility();
      setPayload(response.data);
    } catch {
      // Fail open: keep every filter visible rather than hide UI on a blip.
      setPayload(null);
    } finally {
      setReady(true);
    }
  }, [user]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user) { setPayload(null); setReady(false); return; }
      try {
        const response = await coreAPI.getFilterVisibility();
        if (!cancelled) setPayload(response.data);
      } catch {
        if (!cancelled) setPayload(null);
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, [user, viewKey]);

  const value = useMemo<FilterVisibilityContextType>(() => ({
    flags: payload ? toFlags(payload.flags) : ALL_VISIBLE,
    detected: payload ? toFlags(payload.detected) : ALL_VISIBLE,
    overrides: payload?.overrides ?? {},
    editable: payload?.editable ?? false,
    ready,
    refresh: load,
  }), [payload, ready, load]);

  return <FilterVisibilityContext.Provider value={value}>{children}</FilterVisibilityContext.Provider>;
}

/**
 * The full filter-visibility state. Outside a provider (unit tests, isolated
 * renders) it answers "everything visible", so a panel never needs a mock to
 * render its legacy shape.
 *
 * @returns flags, detected defaults, declared overrides, editability, ready, refresh
 */
export function useFilterVisibilityContext(): FilterVisibilityContextType {
  return useContext(FilterVisibilityContext);
}
